import { useClient, useMutation, useQuery } from '@weenat/client'
import { useIntl } from '@weenat/wintl'
import { Redirect, useNavigate, useParams } from 'app/routx-router'
import Button from 'app/src/kit/Button'
import EmptyStateWithRipple from 'app/src/kit/empty-state/EmptyStateWithRipple'
import Icons from 'app/src/kit/Icons'
import LoadingCircle from 'app/src/kit/loaders/LoadingCircle'
import Text from 'app/src/kit/Text'
import { useToken } from 'app/state'
import isNil from 'lodash-es/isNil'
import { useEffect, useState } from 'react'
import { styled } from 'styled-components'

export const ResendEmailLink = () => {
  const { t } = useIntl()
  const client = useClient()
  const [token] = useToken()

  // Not using context on purpose to avoid crash
  // because if the user is not logged in, the provider won't be in the component tree
  const meRequest = useQuery(client.me.get(), { enabled: !isNil(token) })
  const me = meRequest.data

  const [sendVerifyEmail, sendVerifyEmailState] = useMutation(client.auth.sendVerificationEmail())

  return !isNil(me) ? (
    sendVerifyEmailState.isPending ? (
      <LoadingCircle size='lg' color={'grayscale.black'} />
    ) : sendVerifyEmailState.isSuccess ? (
      <Text $fontWeight='medium' $color='feedback.success.500' $textAlign='center'>
        {t('auth.verify_email_sent', { email: me.email })}
      </Text>
    ) : sendVerifyEmailState.isError ? (
      <Text $fontWeight='medium' $color='feedback.error.500' $textAlign='center'>
        {t('errors.unknown')}
      </Text>
    ) : (
      <Text
        $fontWeight='medium'
        $color='primary.500'
        $textAlign='center'
        onClick={() => sendVerifyEmail({ email: me.email })}
        style={{ cursor: 'pointer' }}
      >
        {t('auth.verify_email_resend')}
      </Text>
    )
  ) : null
}

const StyledButton = styled(Button)`
  align-self: center;
`

export const BackButton = () => {
  const { t } = useIntl()
  const [token] = useToken()
  const nav = useNavigate()

  return (
    <StyledButton
      importance='sd'
      variant='compact'
      onPress={() => {
        nav('/')
      }}
    >
      {t(isNil(token) ? 'actions.go_back_to_login' : 'actions.go_back_to_app')}
    </StyledButton>
  )
}

const VerifyEmail: FC = () => {
  const { t } = useIntl()
  const client = useClient()
  const { verifyEmailKey } = useParams()

  const [errorCode, setErrorCode] = useState<number | undefined>(undefined)

  const [verifyEmail, verifyEmailState] = useMutation(client.auth.verifyEmail(), {
    keysToInvalidate: [client.me.get().key],
    onError: (e) => {
      setErrorCode(e.status)
    }
  })

  // Verify the email on mount
  useEffect(
    () => {
      if (!isNil(verifyEmailKey)) {
        verifyEmail({ key: verifyEmailKey })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <Flex
      $flexDirection='column'
      $p={'lg'}
      $gap='lg'
      $width={480}
      $height='fit-content'
      $mx='auto'
      $alignItems='center'
      $justifyContent='center'
    >
      {verifyEmailState.isPending ? (
        <EmptyStateWithRipple title={t('status.verifying')} Icon={Icons.Wrench}>
          <LoadingCircle size='lg' color={'grayscale.black'} />
        </EmptyStateWithRipple>
      ) : verifyEmailState.isSuccess ? (
        <Redirect to='/verify-email/success' />
      ) : verifyEmailState.isError ? (
        <Redirect to='/verify-email/error' search={{ errorCode }} />
      ) : null}
    </Flex>
  )
}

export const Component = VerifyEmail
